
import { create } from 'zustand';
import { MAP_STYLES } from '../constants';
import { getCurrentLocation } from '../services/location';
import { captureSilentError } from '../services/posthog';

interface MapState {
    mapContainer: HTMLDivElement | null;
    mapInstance: google.maps.Map | null;
    isMapReady: boolean;
    isMapError: boolean;
    
    currentLocation: { lat: number; lng: number };
    hasLocationAccess: boolean;
    isUserPanned: boolean;
    isLoadingLocation: boolean;
    locationError: string | null;

    // Preview Logic
    previewPin: { lat: number; lng: number; name: string } | null;
    showPreviewPin: (location: { lat: number; lng: number }, name: string) => void;
    clearPreviewPin: () => void;

    setMapContainer: (el: HTMLDivElement | null) => void;
    setMapInstance: (map: google.maps.Map | null) => void;
    setIsMapReady: (ready: boolean) => void;
    setIsMapError: (error: boolean) => void;
    setLocationError: (error: string | null) => void;
    
    setIsUserPanned: (panned: boolean) => void;
    
    requestUserLocation: () => Promise<void>;
    
    initMap: (container: HTMLDivElement, initialLocation: { lat: number; lng: number }) => void;
    reset: () => void;
    
    _dragListener: google.maps.MapsEventListener | null;
}

export const useMapStore = create<MapState>((set, get) => ({
    mapContainer: null,
    mapInstance: null,
    isMapReady: false,
    isMapError: false,
    
    currentLocation: { lat: 51.5074, lng: -0.1278 }, // Default London
    hasLocationAccess: false,
    isUserPanned: false,
    isLoadingLocation: false,
    locationError: null,
    
    _dragListener: null,
    previewPin: null,

    showPreviewPin: (location, name) => set({ previewPin: { ...location, name } }),
    clearPreviewPin: () => set({ previewPin: null }),

    setMapContainer: (el) => set({ mapContainer: el }),
    setMapInstance: (map) => set({ mapInstance: map }),
    setIsMapReady: (ready) => set({ isMapReady: ready }),
    setIsMapError: (error) => set({ isMapError: error }),
    setLocationError: (error) => set({ locationError: error }),
    
    setIsUserPanned: (panned) => set({ isUserPanned: panned }),

    initMap: (container, initialLocation) => {
        const { mapInstance, setIsUserPanned } = get();
        if (mapInstance) return;

        try {
            const map = new window.google.maps.Map(container, {
                center: initialLocation,
                zoom: 13,
                styles: MAP_STYLES,
                disableDefaultUI: true,
                zoomControl: false,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                backgroundColor: '#FEFBF6',
            });
            
            const listener = map.addListener('dragend', () => {
                const center = map.getCenter();
                if (!center) return;
                setIsUserPanned(true);
            });
            
            set({ 
                mapInstance: map, 
                isMapReady: true,
                _dragListener: listener 
            });
        } catch (e) {
            captureSilentError("useMapStore.initMap", e);
            set({ isMapError: true });
        }
    },

    reset: () => {
        const { _dragListener } = get();
        if (_dragListener) {
            google.maps.event.removeListener(_dragListener);
        }

        set({
            mapContainer: null,
            mapInstance: null,
            isMapReady: false,
            isMapError: false,
            currentLocation: { lat: 51.5074, lng: -0.1278 },
            hasLocationAccess: false,
            isUserPanned: false,
            isLoadingLocation: false,
            locationError: null,
            _dragListener: null,
            previewPin: null
        });
    },



    requestUserLocation: async () => {
        set({ isLoadingLocation: true, locationError: null });
        try {
            const result = await getCurrentLocation();
            if (result.success && result.coords) {
                set({ 
                    currentLocation: result.coords,
                    hasLocationAccess: true,
                    isUserPanned: false,
                    isLoadingLocation: false,
                    locationError: null
                });
                
                // If map exists, pan to it (can also be done in component effect)
                const { mapInstance } = get();
                if (mapInstance) {
                    mapInstance.panTo(result.coords);
                }
            } else {
                set({ 
                    hasLocationAccess: false, 
                    isLoadingLocation: false,
                    locationError: result.message || "Location access denied"
                });
            }
        } catch (e) {
            captureSilentError("useMapStore.requestUserLocation", e);
            set({ 
                hasLocationAccess: false, 
                isLoadingLocation: false,
                locationError: "Failed to access location"
            });
        }
    }
}));
